import { NextRouter } from "next/router"
import { Session } from "@/types/auth"
import { Attachment, CustomerMessageSubtype } from "@/types/chat"
import { CollectionRef, isCollectionRef } from "@/types/collection"
import {
  EmbeddedFrameProps,
  EmbeddedProps,
  InitEmbeddedChatProps,
} from "@/types/embedded"
import {
  InspirationRef,
  isInspirationRef,
  isPlaceRef,
  PlaceRef,
} from "@/types/entity"
import { FlightOfferUnavailableException } from "@/types/flights/exceptions"
import {
  FlightBookingMode,
  PRE_SUBMIT_STATUSES,
} from "@/types/flights/flight-booking"
import {
  getAnalyticsProperties,
  getOrganizationUrl,
  Organization,
} from "@/types/organization"
import { TripRef } from "@/types/trip"
import { isUserRef, UserRef } from "@/types/user"
import { Honeybadger } from "@honeybadger-io/react"
import { createAsyncThunk } from "@reduxjs/toolkit"
import isEqual from "lodash/isEqual"
import nProgress from "nprogress"

import {
  AmplitudeInitProps,
  getUtmParams,
  initAmplitude,
  sendAnalyticsEvent,
  setAnalyticsUserProperties,
} from "@/lib/analytics/amplitude-utils"
import { getBrowserCapabilities } from "@/lib/auth/browser-capabilities"
import {
  getSession,
  refreshTokenIfNeeded,
  systemSignOut,
  tryGetSession,
} from "@/lib/auth/client"
import { fetchChat } from "@/lib/chat"
import { ChatApi } from "@/lib/chat-api"
import { buildClientAxios } from "@/lib/client-axios"
import { showRegisterGateForCreatorReferral } from "@/lib/dialog-utils"
import { EmbeddedAuthApi } from "@/lib/embedded-auth-api"
import {
  clearEmbeddedAuthTokens,
  storeEmbeddedTokens,
} from "@/lib/embedded/token-storage"
import { env } from "@/lib/env"
import { FlightApi } from "@/lib/flight-api"
import { buildDepartureSearchUrl } from "@/lib/flight-booking-utils"
import { fetchGuideProps } from "@/lib/inspiration"
import { InspirationApi } from "@/lib/inspiration-api"
import { isMicrositeHost, micrositeOrgid } from "@/lib/is-embedded"
import { safeJsonStringify, tryJsonParse } from "@/lib/json-utils"
import { getLanguageName, parseLocale } from "@/lib/locale"
import { setResolvedLocale } from "@/lib/locale-headers"
import { isMarketingPath } from "@/lib/marketing-routes"
import { getAttachmentMetadata } from "@/lib/media-thumbnail-utils"
import { mtLocation } from "@/lib/mt-location"
import { OrganizationApi } from "@/lib/organization-api"
import { fetchProfileProps, fetchPublicProfileProps } from "@/lib/profile"
import { matchRoute, routes } from "@/lib/routes"
import { fetchSettingsProps } from "@/lib/settings"
import { ShortUrlApi } from "@/lib/short-url-api"
import { itineraryApi } from "@/lib/storage/api/itinerary-api"
import { placeApi } from "@/lib/storage/api/place-api"
import {
  selectEmbeddedGeoHint,
  selectOrganizationContext,
  setEmbedded,
} from "@/lib/storage/app-slice"
import { applyPageProps, initApp } from "@/lib/storage/app-thunks"
import { showEmailVerifyStep } from "@/lib/storage/auth-thunks"
import { goToChat, initChat } from "@/lib/storage/chat-thunks"
import collectionSlice from "@/lib/storage/collection-slice"
import {
  selectEmbeddedIsInitApp,
  selectEmbeddedIsInitChat,
  selectEmbeddedProps,
  selectEmbeddedView,
  selectLocaleChanged,
  setEmbeddedIsInitApp,
  setEmbeddedIsInitChat,
  setEmbeddedProps,
  setLocaleChanged,
  setRenderCreateAnonTurnstile,
  setView,
  type HotelRoomsView,
} from "@/lib/storage/embedded-slice"
import { initGuide, initInspiration } from "@/lib/storage/inspiration-thunks"
import { askBotToGenerateItinerary } from "@/lib/storage/itinerary-generation-slice"
import { selectLocale } from "@/lib/storage/locale-selectors"
import { setEmbeddedLocale } from "@/lib/storage/locale-slice"
import { initialBotGreeting, sendMessage } from "@/lib/storage/message-thunks"
import { beginPageInitCycle } from "@/lib/storage/page-init-gate"
import { initProfile } from "@/lib/storage/profile-thunks"
import { initPublicProfile } from "@/lib/storage/public-profile-thunks"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { loadPlaceRoute, openTripRoute } from "@/lib/storage/route-init-thunks"
import { initSaved } from "@/lib/storage/saved-thunks"
import { setBurgerOpen } from "@/lib/storage/site-nav-slice"
import {
  selectShowClaimHandleDialog,
  selectUser,
  selectUserPermissions,
  userSlice,
} from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import { compact } from "@/lib/utils/collections"
import { tripRefFromUrl } from "@/lib/utils/page"
import { webStorage } from "@/lib/web-storage"
import {
  CfTokenValue,
  DISABLED_TOKEN,
  fetchAnonTurnstileToken,
} from "@/components/create-anon-turnstile"
import { ShareProps, ShareType } from "@/components/share"
import { initGTMOrganization } from "../analytics/gtm-utils"
import { fetchExploreProps } from "../explore"
import { tripsApi } from "./api/trips-api"
import { openAfterStream, openEntitySheet } from "./detail-sheet-slice"
import { initExplore } from "./explore-thunks"
import { handleNewChat } from "./site-nav-thunks"
import tripSlice from "./trip-slice"

// Helper to parse embedded params from hash or URL in development mode.
// In non-production, if no hash params exist, extracts orgid from pathname
// and merges in any query params as embedded props.
function parseEmbeddedParams(
  hash: string,
  pathname: string,
  query: NextRouter["query"]
): EmbeddedProps {
  // for microsite subdomains: orgid always comes from hostname, not hash or path
  if (isMicrositeHost()) {
    const mtUrl = Array.isArray(query["mt:url"])
      ? query["mt:url"][0]
      : query["mt:url"]
    return {
      orgid: micrositeOrgid() ?? undefined,
      open: true,
      ...(mtUrl ? { route: mtUrl } : {}),
    }
  }

  let params = tryJsonParse<EmbeddedProps>(
    decodeURIComponent(hash.split("#")[1] ?? "")
  )

  // For testing in development: if no hash params, extract orgid from URL and assume open=true
  if (!hash && env("NODE_ENV") !== "production") {
    const pathMatch = /^\/embedded\/([^/?]+)/.exec(pathname)
    if (pathMatch) {
      // Convert query params to simple string object (take first value if array)
      const queryParams: Record<string, string> = {}
      Object.entries(query).forEach(([key, value]) => {
        if (value) {
          queryParams[key] = Array.isArray(value) ? value[0] : value
        }
      })
      params = {
        orgid: pathMatch[1],
        open: true,
        ...queryParams,
      }
    }
  }

  return params ?? {}
}

// `nocookie` is a deny list of cookie types the user declined, e.g. "functional,advertising"
export function cookieConsent(nocookie?: string): {
  functional: boolean
  advertising: boolean
} {
  const declined = (nocookie ?? "").split(",").map((c) => c.trim())
  return {
    functional: !declined.includes("functional"),
    advertising: !declined.includes("advertising"),
  }
}

// Apply a host-supplied locale (the embedded URL/hash param or a `mindtrip.locale` postMessage)
export function applyHostLocale(
  dispatch: AppDispatch,
  locale: string | undefined
): void {
  dispatch(setEmbeddedLocale(parseLocale(locale)))
}

export const handleParamChange = createAsyncThunk<
  void,
  { hash: string; router: NextRouter },
  { dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/handleParamChange",
  async ({ hash, router }, { dispatch, getState }) => {
    const isInitApp = selectEmbeddedIsInitApp(getState())

    // app is not initialized yet, skip this will get called again after init
    if (!isInitApp) {
      void dispatch(initEmbeddedApp({ router }))
      return
    }

    const params = parseEmbeddedParams(hash, router.pathname, router.query)

    if (params && !isEqual(params, selectEmbeddedProps(getState()))) {
      const prev = selectEmbeddedProps(getState())
      dispatch(setEmbeddedProps(params))

      const organizationContext = selectOrganizationContext(getState())
      // no tracking is initialized when functional cookie is declined
      const trackingAllowed =
        !params.preview && cookieConsent(params.nocookie).functional

      // this means the slide out is being open
      if (!prev?.open && params.open) {
        const user = selectUser(getState())
        const permissions = selectUserPermissions(getState())

        if (organizationContext) {
          if (trackingAllowed) {
            // initialize amplitude when slide out is opened, not before
            const uuid = user.uuid
            const provider = user.provider
            const handle = user.handle
            const status =
              uuid && provider !== "anonymous" && handle
                ? "authenticated"
                : "unauthenticated"

            const ampProps: AmplitudeInitProps = {
              type: "initProps",
              uuid: uuid,
              provider: provider,
              handle: handle,
              status: status,
              orgid: organizationContext.orgid,
              permissions: permissions,
              // Most likely user will not have these properties.
              has_thatch_data: user.has_thatch_data,
              is_from_thatch: user.is_from_thatch,
              is_thatch_creator: user.is_thatch_creator,
              is_crawler: user.is_crawler,
              session_tracking: true, // Session events add to expense so only do for DMOs so we can track time on site
            }

            initGTMOrganization(organizationContext)
            initAmplitude(ampProps)

            setAnalyticsUserProperties({
              type: "utmProps",
              utmSource: params.utm_source,
              utmMedium: params.utm_medium,
              utmCampaign: params.utm_campaign,
              utmContent: params.utm_content,
              utmTerm: params.utm_term,
            })
          }
          // Force the host locale into the resolver + Weglot sink; see applyHostLocale.
          // localeChanged = did the visible language change? Compare display names so pt
          // vs pt-BR counts but region-only changes (en-US/en-GB) don't re-greet.
          const beforeLanguage = getLanguageName(selectLocale(getState()))
          applyHostLocale(dispatch, params.locale)
          const afterLanguage = getLanguageName(selectLocale(getState()))
          dispatch(setLocaleChanged(beforeLanguage !== afterLanguage))
        }
      }

      if (params.open) {
        // every open that changes the frame hash reaches here; emit open event
        if (organizationContext && trackingAllowed) {
          sendAnalyticsEvent({
            name: "EmbeddedWidgetOpen",
            properties: getAnalyticsProperties(organizationContext, params),
          })
        }

        // if hash params have changed, re-init the chat to pick up new entry point prompts
        dispatch(setEmbeddedIsInitChat(false))

        await dispatch(
          handleRouteChange({ router, url: params.route ?? "/chat" })
        )

        window.parent.postMessage("mindtrip.view-loaded", "*")

        const view = selectEmbeddedView(getState())
        const user = selectUser(getState())
        const claimHandleOpen = selectShowClaimHandleDialog(getState())
        if (
          user?.provider === "email" &&
          !user.email_validated &&
          view.type !== "verify-email" &&
          !claimHandleOpen
        ) {
          void dispatch(showEmailVerifyStep())
        }
      }
    }
  }
)

export const initEmbeddedApp = createAsyncThunk<
  void,
  { router: NextRouter },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/initEmbeddedApp",
  async ({ router }, { dispatch, getState, rejectWithValue }) => {
    const params = parseEmbeddedParams(
      mtLocation.hash,
      router.pathname,
      router.query
    )

    dispatch(setEmbedded(true))
    dispatch(setBurgerOpen(false))

    if (!params.open) {
      // if not opening right away via mt:url param,
      // we will re-init later when called from handleParamChange
      return
    }

    applyHostLocale(dispatch, params.locale)
    setResolvedLocale(selectLocale(getState()))

    const clientAxios = buildClientAxios()
    const embeddedApi = new EmbeddedAuthApi(clientAxios)
    const organizationApi = new OrganizationApi(clientAxios)
    const orgid = params.orgid
    if (!orgid) {
      console.error("orgid is required")
      toastManager.add({
        type: "error",
        description: "Please include orgid in mindtrip embedded url",
      })
      return rejectWithValue("orgid is required")
    }

    let session: Session | null = null
    let organization: Organization | null = null
    try {
      // Try to refresh token if needed and get session
      await refreshTokenIfNeeded()
      session = await tryGetSession()

      // if org changed, discard the stale session
      if (
        session &&
        !session.is_legacy &&
        session.orgid &&
        session.orgid !== orgid
      ) {
        clearEmbeddedAuthTokens()
        session = null
      }

      // If no session exists, create anonymous user
      if (!session) {
        clearEmbeddedAuthTokens()
        let cfTokenValue: CfTokenValue = DISABLED_TOKEN
        if (env("NEXT_PUBLIC_EMBEDDED_CF_TURNSTILE_REQUIRED") === "true") {
          dispatch(setRenderCreateAnonTurnstile(true))
          try {
            cfTokenValue = await fetchAnonTurnstileToken()
          } catch (error) {
            console.error("Failed to fetch Turnstile token:", error)
            dispatch(setView({ type: "error" }))
            return rejectWithValue("Turnstile verification failed")
          } finally {
            dispatch(setRenderCreateAnonTurnstile(false))
          }
        }

        const hostname = params.orgUrl
          ? new URL(getOrganizationUrl(params.orgUrl)).hostname
          : ""
        const res = await embeddedApi.createAnonymousUser(
          orgid,
          hostname,
          cfTokenValue,
          params.code,
          params.nocookie
        )
        storeEmbeddedTokens(res)

        // Get the session for the newly created anonymous user
        session = await getSession()
      }
      // always re-fetch demo/pending orgs to enable rapid testing of config changes
      if (session?.organization?.status === "live") {
        organization = session.organization
      } else {
        organization = await organizationApi.getOrganization(orgid)
      }

      // clear the error attempts
      webStorage.removeItem("embedded.mindtrip.auth.attempts")
    } catch (e) {
      // if we fail to fetch the user, we need to clear the auth data and retry again
      // but to prevent an infinite loop, we'll only retry 3 times. Could be a case where the user has some browser
      // setting that prevents the user from being fetched or an extension that is blocking the request

      const initializationError =
        e instanceof Error ? e : new Error("Embedded initialization failed")
      const attempts = parseInt(
        webStorage.getItem("embedded.mindtrip.auth.attempts") ?? "0"
      )

      try {
        const capabilities = getBrowserCapabilities()
        const storageUnavailable =
          !capabilities.localStorageAvailable &&
          !capabilities.sessionStorageAvailable

        if (storageUnavailable) {
          Honeybadger.notify(initializationError)
          dispatch(setView({ type: "check-settings" }))
          window.parent.postMessage("mindtrip.view-loaded", "*")
          return
        }
      } catch (capabilityError) {
        if (attempts > 2) {
          Honeybadger.notify(initializationError)
          Honeybadger.notify(
            capabilityError instanceof Error
              ? capabilityError
              : new Error("Browser capability classification failed")
          )
          dispatch(setView({ type: "error" }))
          window.parent.postMessage("mindtrip.view-loaded", "*")
          return
        }
      }

      if (attempts > 2) {
        Honeybadger.notify(initializationError)
        dispatch(setView({ type: "error" }))
        window.parent.postMessage("mindtrip.view-loaded", "*")
      } else {
        // sign out
        void systemSignOut({ embeddedAttempts: attempts + 1 })
      }
      return
    }

    beginPageInitCycle()

    const pageProps = compact({
      // todo set these from the page props
      session,
      supportedBrowser: true,
      enableImpressions: false,
      appType: "embedded" as const,
      orgid: params.orgid,
      orgUrl: params.orgUrl,
      geoName: params.geoName,
      organization,
      isEmbedded: true,
    })
    await dispatch(initApp(pageProps))
    await dispatch(applyPageProps(pageProps))

    dispatch(setEmbeddedIsInitApp(true))

    await dispatch(handleParamChange({ hash: mtLocation.hash, router }))
  }
)

export const initEmbeddedChat = createAsyncThunk<
  void,
  EmbeddedFrameProps & {
    orgid?: string
    chatId?: number | "latest"
  },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>("embeddedSlice/initEmbeddedChat", async (params, { dispatch, getState }) => {
  dispatch(setEmbeddedIsInitChat(true))
  dispatch(setBurgerOpen(false))

  const clientAxios = buildClientAxios()
  const chatApi = new ChatApi(clientAxios)

  const chat = await fetchChat(params.chatId ?? "latest", chatApi)

  const initProps: InitEmbeddedChatProps = compact({
    initChat: chat,
    prompt: params.prompt?.trim(),
    intro: params.intro?.trim(),
    button: params.button?.trim(),
    link: params.link?.trim(),
    hint: params.hint?.trim(),
    vibes: params.vibes,
    id: params.id,
  })

  // this needs to be set before init, else the initial bot greeting won't stream properly
  // this causes some screen jank, todo move the init bot stuff out of init chat and into its own thunk we can call here
  dispatch(
    setView({
      type: "chat",
    })
  )

  // init chat
  await dispatch(initChat(initProps))

  if (params.chatId !== undefined) {
    return
  }

  // logic to initialize embedded chat overlay:
  // set geo hint, generate initial bot greeting, emit events
  const geoHint = selectEmbeddedGeoHint(getState())
  let userMessage = initProps.prompt
  if (!userMessage && initProps.link) {
    // if no prompt provided for a link, use the button text as the prompt
    userMessage = initProps.button
  }
  const guideLink = initProps.link?.match(
    /\b(explore|inspiration)\/.*\/(in-[^/?]+)\b/
  )
  const placeLink = initProps.link?.match(
    /\b(activity|attraction|event|hotel|location|restaurant)\/.*\/((ac|at|ev|re|ho|lo)-[^/?]+)\b/
  )
  const localeChanged = selectLocaleChanged(getState())
  const isSuggestedQuestion =
    !!initProps.prompt && !initProps.intro && !params.preview
  const subType = isSuggestedQuestion
    ? "suggested-question"
    : guideLink || placeLink
      ? "external-chat-cta"
      : undefined

  if (userMessage) {
    const language = getLanguageName(selectLocale(getState()))
    const languageHint = language
      ? ` The customer prefers to speak in ${language}.`
      : ""
    if (guideLink) {
      // we have a message and a link to an internal explore page guide; use it to seed an itinerary
      const inspirationRef = guideLink[2] as InspirationRef
      const inspirationApi = new InspirationApi(buildClientAxios())
      const newChat =
        await inspirationApi.generateChatTripAndItinerary(inspirationRef)
      dispatch(userSlice.actions.setChat(newChat))

      void dispatch(
        itineraryApi.endpoints.getItinerary.initiate({
          tripRef: newChat.trip.ref,
        })
      )

      await dispatch(
        sendMessage({
          messageString: userMessage,
          fromInspiration: inspirationRef,
          subType,
        })
      )

      dispatch(openAfterStream({ kind: "trip", tripRef: newChat.trip.ref }))
    } else if (placeLink) {
      const placeRef = placeLink[2] as PlaceRef
      const entity = await dispatch(
        placeApi.endpoints.getPlace.initiate({ placeRef })
      ).unwrap()

      const locationDetails = [
        entity.city !== entity.name ? entity.city : null,
        entity.state !== entity.name ? entity.state : null,
        entity.region !== entity.name ? entity.region : null,
        entity.country !== entity.name ? entity.country : null,
      ]
        .filter(Boolean)
        .join(", ")
      const messageHint = `${entity.name} is a ${entity.type} in ${locationDetails}.`

      // always create a new chat for place entry point
      const { chat } = await chatApi.createChat()
      dispatch(userSlice.actions.setChat(chat))
      await dispatch(
        sendMessage({
          messageString: userMessage,
          hint: messageHint + languageHint,
          subType,
        })
      )
    } else {
      // otherwise, send initial user message from partner input field w/ optional Start Anywhere link
      const intro = initProps.intro
      const userHint = initProps.hint ? `${initProps.hint}. ` : ""
      const locationHint = geoHint ? `Location: ${geoHint}.` : ""
      if (intro) {
        userMessage = `${intro.replace(/[\s.…]+$/, "")}: ${userMessage}`
      }
      const messageBody: {
        messageString: string
        hint?: string
        attachments?: Attachment[]
        subType?: CustomerMessageSubtype
      } = {
        messageString: userMessage,
        hint: userHint + locationHint + languageHint,
        subType,
      }
      if (initProps.link) {
        const attachmentMetadata = await getAttachmentMetadata(initProps.link)
        messageBody.attachments = [
          {
            url: initProps.link,
            type: "link",
            metadata: attachmentMetadata || {},
          },
        ]
      }
      // always create a new chat for message entry point
      const { chat } = await chatApi.createChat()
      dispatch(userSlice.actions.setChat(chat))
      await dispatch(sendMessage(messageBody))
    }
  } else if (initProps.vibes) {
    // vibe and no message: generate an itinerary from vibes in a new chat
    const { chat } = await chatApi.createChat()
    dispatch(userSlice.actions.setChat(chat))

    const vibes = initProps.vibes.map((vibe) => ({ title: vibe }))
    const { data: name } = await dispatch(
      tripsApi.endpoints.generateTripNameFromStops.initiate({
        tripRef: chat.trip.ref,
        tripUpdate: {
          major_stops: chat.trip.major_stops?.length
            ? chat.trip.major_stops
            : chat.trip.destination_entity_ref
              ? [
                  {
                    entity_ref: chat.trip.destination_entity_ref,
                    days: undefined,
                  },
                ]
              : [],
          roadtrip: chat.trip.roadtrip,
        },
      })
    )
    const updateResult = await dispatch(
      tripsApi.endpoints.updateTrip.initiate({
        tripRef: chat.trip.ref,
        tripUpdate: {
          vibes,
          name, // placeholder name, later bot streaming will update it
        },
      })
    )
    if (updateResult.data) {
      await dispatch(
        tripsApi.endpoints.getTrip.initiate(chat.trip.ref, {
          forceRefetch: true,
        })
      )
    }

    void dispatch(askBotToGenerateItinerary())
  } else if (initProps.initChat.messages.length === 0) {
    // generate an initial greeting based on geo restriction
    await dispatch(initialBotGreeting())
  } else if (localeChanged) {
    // start a new chat with greeting if the parent site locale has changed
    const { chat } = await chatApi.createChat()
    dispatch(userSlice.actions.setChat(chat))
    await dispatch(initialBotGreeting())
  }
})

export const showChatView = createAsyncThunk<
  void,
  {
    router: NextRouter
    chatId?: number | "latest"
    tripSheet?: boolean
    tripSheetEventId?: number
    placeRef?: PlaceRef
  },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showChatView",
  async (
    { router, chatId = "latest", tripSheet, tripSheetEventId, placeRef },
    { dispatch, getState }
  ) => {
    const isChatInit = selectEmbeddedIsInitChat(getState())
    if (isChatInit) {
      dispatch(setView({ type: "chat" }))

      await dispatch(
        goToChat(
          compact({
            router,
            chatId,
            tripSheet,
            tripSheetEventId,
            skipRouteCheck: true,
          })
        )
      )

      if (placeRef && isPlaceRef(placeRef)) {
        const newPlace = await dispatch(
          placeApi.endpoints.getPlace.initiate({ placeRef })
        ).unwrap()
        void dispatch(
          openEntitySheet({
            entity: newPlace,
            entitySheetTrackingContext: "ShowChatView",
          })
        )
      }
    } else {
      const props = selectEmbeddedProps(getState())
      if (props) {
        await dispatch(
          initEmbeddedChat({
            ...props,
            chatId: chatId !== "latest" ? chatId : undefined,
          })
        )
      }
    }
  }
)

export const showSavedView = createAsyncThunk<
  void,
  void,
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>("embeddedSlice/showSavedView", async (_, { dispatch }) => {
  dispatch(setView({ type: "saved" }))
  dispatch(setBurgerOpen(false))

  dispatch(
    setView({
      type: "saved",
    })
  )
  await dispatch(initSaved())
})

export const showGuideView = createAsyncThunk<
  void,
  {
    inspirationRef: InspirationRef
    utm_campaign?: string
    utm_source?: string
    utm_medium?: string
  },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showGuideView",
  async (
    { inspirationRef, utm_source, utm_medium, utm_campaign },
    { dispatch, getState }
  ) => {
    // clear stale guide content while loading new guide
    dispatch(setView({ type: "empty" }))

    const guideProps = await fetchGuideProps(
      buildClientAxios(),
      inspirationRef,
      false
    )

    const user = selectUser(getState())
    const showRegisterGate = showRegisterGateForCreatorReferral(
      user,
      utm_campaign,
      utm_source,
      utm_medium
    )

    await dispatch(initGuide(guideProps))

    dispatch(
      setView({
        type: "guide",
        props: {
          ...guideProps,
          showRegisterGate,
          isEditable: false,
          canAdmin: false,
        },
      })
    )
  }
)

export const showInspirationView = createAsyncThunk<
  void,
  { router: NextRouter },
  { dispatch: AppDispatch }
>("embeddedSlice/showInspirationView", async ({ router }, { dispatch }) => {
  await dispatch(initInspiration({ router }))

  dispatch(
    setView({
      type: "inspiration",
    })
  )
})

export const showExploreView = createAsyncThunk<
  void,
  { router: NextRouter },
  { dispatch: AppDispatch }
>("embeddedSlice/showExploreView", async ({ router }, { dispatch }) => {
  const exploreProps = await fetchExploreProps(buildClientAxios(), router.query)
  await dispatch(initExplore({ ...exploreProps, router }))

  dispatch(
    setView({
      type: "explore",
    })
  )
})

export const showTripsView = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("embeddedSlice/showTripsView", async (_, { dispatch }) => {
  sendAnalyticsEvent("ViewTripsPage")

  dispatch(
    setView({
      type: "trips",
    })
  )
})

export const showTripView = createAsyncThunk<
  void,
  { ref: TripRef },
  { dispatch: AppDispatch; state: RootState }
>("embeddedSlice/showTripView", async ({ ref }, { dispatch }) => {
  dispatch(
    setView({
      type: "trip",
      props: { ref },
    })
  )

  await dispatch(openTripRoute(ref)).unwrap()
})

export const showCollectionView = createAsyncThunk<
  void,
  { ref: CollectionRef },
  { dispatch: AppDispatch; state: RootState }
>("embeddedSlice/showCollectionView", async ({ ref }, { dispatch }) => {
  dispatch(collectionSlice.actions.setCurrentCollectionRef(ref))
  dispatch(
    setView({
      type: "collection",
      props: { collectionRef: ref },
    })
  )
})

export const showPublicProfileView = createAsyncThunk<
  void,
  string,
  { dispatch: AppDispatch }
>("embeddedSlice/showPublicProfileView", async (handle, { dispatch }) => {
  const publicProfileProps = await fetchPublicProfileProps(
    buildClientAxios(),
    handle
  )

  await dispatch(initPublicProfile(publicProfileProps))

  dispatch(
    setView({
      type: "public-profile",
      props: { handle },
    })
  )
})

export const showProfileView = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("embeddedSlice/showProfileView", async (_, { dispatch, getState }) => {
  const profileProps = await fetchProfileProps(buildClientAxios())
  const user = selectUser(getState())
  const permissions = selectUserPermissions(getState()) ?? []

  await dispatch(
    initProfile({
      initUser: user,
      ...profileProps,
    })
  )

  dispatch(
    setView({
      type: "profile",
      props: {
        initUser: user,
        permissions,
        ...profileProps,
      },
    })
  )
})

export const showShareView = createAsyncThunk<
  void,
  Omit<ShareProps, "initUser">,
  { dispatch: AppDispatch; state: RootState }
>("embeddedSlice/showShareView", async (shareProps, { dispatch, getState }) => {
  const user = selectUser(getState())

  dispatch(
    setView({
      type: "share",
      props: compact({
        initUser: user,
        ...shareProps,
      }),
    })
  )
})

export const showPlaceView = createAsyncThunk<
  void,
  {
    placeRef: PlaceRef
    utm_campaign?: string
    utm_source?: string
    utm_medium?: string
    router: NextRouter
  },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showPlaceView",
  async (
    { placeRef, router, utm_campaign, utm_source, utm_medium },
    { dispatch, getState }
  ) => {
    const place = await dispatch(loadPlaceRoute({ placeRef })).unwrap()

    if (place == null) {
      await dispatch(showChatView({ router }))
      return
    }

    const user = selectUser(getState())

    const showRegisterGate = showRegisterGateForCreatorReferral(
      user,
      utm_campaign,
      utm_source,
      utm_medium
    )

    dispatch(
      setView({
        type: "place",
        props: { place, showRegisterGate },
      })
    )
  }
)

export const showHotelRoomsView = createAsyncThunk<
  void,
  HotelRoomsView["props"],
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>("embeddedSlice/showHotelRoomsView", async (props, { dispatch }) => {
  dispatch(setView({ type: "hotel-rooms", props }))
})

// client-side equivalent of the /flights/booking/[id] SSR fetch; embedded mode
// has no getServerSideProps, so we fetch the booking here and hydrate the view
export const showFlightBookingView = createAsyncThunk<
  void,
  { bookingId: number; router: NextRouter },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showFlightBookingView",
  async ({ bookingId, router }, { dispatch }) => {
    const flightApi = new FlightApi(buildClientAxios())
    const bookingMode: FlightBookingMode = "sandbox"

    try {
      const [flightBooking, formOptions, mode] = await Promise.all([
        flightApi.getFlightBooking(bookingId),
        flightApi.getFlightBookingOptions(),
        flightApi.getFlightBookingMode(),
      ])

      if (flightBooking.status === "error") {
        dispatch(
          setView({
            type: "flight-booking",
            props: {
              inFlightBooking: flightBooking,
              inError:
                "An error occurred during booking. Please contact support: support@mindtrip.ai.",
              inBookingMode: mode,
              vendorCheckoutUrl: null,
            },
          })
        )
        return
      }

      if (flightBooking.status === "expired") {
        dispatch(
          setView({
            type: "flight-booking",
            props: {
              inFlightBooking: flightBooking,
              inError: "Flight offer has expired",
              inExpired: true,
              inBookingMode: mode,
              vendorCheckoutUrl: null,
            },
          })
        )
        return
      }

      let vendorCheckoutUrl: string | null = null
      if (
        flightBooking.flight_itinerary.vendor === "sabre" &&
        flightBooking.id &&
        PRE_SUBMIT_STATUSES.includes(flightBooking.status)
      ) {
        vendorCheckoutUrl = flightBooking.vendor_checkout?.iframe_url ?? null
      }

      dispatch(
        setView({
          type: "flight-booking",
          props: {
            inFlightBooking: flightBooking,
            inFormOptions: formOptions,
            inBookingMode: mode,
            vendorCheckoutUrl,
          },
        })
      )
    } catch (e) {
      if (
        e instanceof FlightOfferUnavailableException &&
        e.data?.flight_itinerary
      ) {
        await router.push(
          buildDepartureSearchUrl(e.data.flight_itinerary, { expired: true })
        )
        return
      }
      dispatch(
        setView({
          type: "flight-booking",
          props: {
            inError: "Error retrieving flight booking information",
            inBookingMode: bookingMode,
            vendorCheckoutUrl: null,
          },
        })
      )
    }
  }
)

export const showSettingsView = createAsyncThunk<
  void,
  { section: string },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showSettingsView",
  async ({ section }, { dispatch, getState }) => {
    const settingProps = await fetchSettingsProps(buildClientAxios())
    const user = selectUser(getState())

    dispatch(
      setView({
        type: "settings",
        props: { initUser: user, initSection: section, ...settingProps },
      })
    )
  }
)

export const showJoinTripView = createAsyncThunk<
  void,
  { code: string },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showJoinTripView",
  async ({ code }, { dispatch, getState }) => {
    const user = selectUser(getState())

    dispatch(
      setView({
        type: "join-trip",
        props: {
          invitationCode: code,
          anonymous: user.provider === "anonymous",
        },
      })
    )
  }
)

export const showVerifyEmailView = createAsyncThunk<
  void,
  { code: string },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showVerifyEmailView",
  async ({ code }, { dispatch, getState }) => {
    const user = selectUser(getState())

    dispatch(
      setView({
        type: "verify-email",
        props: {
          code,
          isAnonymous: user.provider === "anonymous",
        },
      })
    )
  }
)

export const showResetPasswordView = createAsyncThunk<
  void,
  { resetPasswordToken: string; callbackUrl?: string },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/showResetPasswordView",
  async ({ resetPasswordToken, callbackUrl }, { dispatch }) => {
    dispatch(
      setView({
        type: "reset-password",
        props: {
          resetPasswordToken,
          callbackUrl,
        },
      })
    )
  }
)

export const handleClose = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("embeddedSlice/handleClose", async (_) => {
  window.parent.postMessage("mindtrip.close-frame", "*")
})

function extractPathAndParams(url: string): [string, URLSearchParams] {
  let path: string
  let searchParams: URLSearchParams

  try {
    const urlObject = new URL(url)
    path = urlObject.pathname
    searchParams = urlObject.searchParams
  } catch (error) {
    // If parsing fails, assume the input is already a path
    const [pathPart, queryPart] = url.split("?")
    path = pathPart.startsWith("/") ? pathPart : "/" + pathPart
    searchParams = new URLSearchParams(queryPart || "")
  }

  return [path, searchParams]
}

let routeChangeAction: { abort: (reason?: string) => void } = {
  abort: () => {},
}

export const handleRouteChange = createAsyncThunk<
  void,
  { router: NextRouter; url: string },
  { dispatch: AppDispatch; state: RootState }
>(
  "embeddedSlice/handleRouteChange",
  async ({ url, router }, { dispatch, abort, getState }) => {
    dispatch(setBurgerOpen(false))
    nProgress.start()
    try {
      routeChangeAction.abort("Route change in progress")
      routeChangeAction = {
        abort,
      }

      // on microsite: keep browser URL in sync with the embedded route
      if (isMicrositeHost()) {
        window.history.replaceState(null, "", url)
      }

      const user = selectUser(getState())
      const [path, queryParams] = extractPathAndParams(url)

      // Paths with no page of their own: Next rewrites or redirects them on
      // the regular site, and rewrites do not apply here.
      if (/^\/chat\/new/i.test(path)) {
        dispatch(setView({ type: "chat" }))
        await dispatch(
          handleNewChat({
            router,
            queryParams,
            skipRouteCheck: true,
          })
        )
        if (/\/trip$/i.test(path)) {
          dispatch(tripSlice.actions.openCreateTripDialog({ showSheet: true }))
        }
        return
      }

      const shareChatPathMatch = /^\/share\/chat\/([^/?]+)/i.exec(path)
      if (shareChatPathMatch) {
        const code = shareChatPathMatch[1]
        const refId = queryParams.get("refId")
        const shareUserRef: UserRef | undefined = isUserRef(refId)
          ? refId
          : undefined
        const shareChatType: ShareType = "share-chat"
        await dispatch(
          showShareView(
            compact({
              id: code,
              code: "",
              type: shareChatType,
              shareUserRef,
              utmProperties: getUtmParams(queryParams),
              assetMode: "",
              partner: "",
              assetHandle: "",
              assetTitle: "",
              assetUrl: "",
              assetDayCount: "",
              assetThumbnailUrl: "",
            })
          )
        )
        return
      }

      const shareTripPathMatch = /^\/share\/trip\/([^/?]+)/i.exec(path)
      if (shareTripPathMatch) {
        const code = shareTripPathMatch[1]
        const refId = queryParams.get("refId")
        const shareUserRef: UserRef | undefined = isUserRef(refId)
          ? refId
          : undefined
        const shareTripType: ShareType = "share-trip"
        await dispatch(
          showShareView(
            compact({
              id: code,
              code: "",
              type: shareTripType,
              shareUserRef,
              utmProperties: getUtmParams(queryParams),
              assetMode: "",
              partner: "",
              assetHandle: "",
              assetTitle: "",
              assetUrl: "",
              assetDayCount: "",
              assetThumbnailUrl: "",
            })
          )
        )
        return
      }

      // Short URLs (/s/:code) — resolve and re-navigate to the target path
      const shortUrlMatch = /^\/s\/([^/?]+)/i.exec(path)
      if (shortUrlMatch) {
        const shortUrlApi = new ShortUrlApi(buildClientAxios())
        const url = await shortUrlApi.resolveCode(shortUrlMatch[1])
        await router.push(url)
        return
      }

      // Magic links: vanity URL logic adapted from vanity/id.tsx and build_trip route from next.config.js
      const vanityUrlMatch = /^\/z\/(.+)/i.exec(path)
      if (vanityUrlMatch) {
        const shortUrlApi = new ShortUrlApi(buildClientAxios())
        const code = vanityUrlMatch[1]
        const url = await shortUrlApi.resolveCode(code)
        const newUrl = new URL(url)
        newUrl.searchParams.append("type", "vanity")
        newUrl.searchParams.append("code", code)
        await router.push(newUrl.toString())
        return
      }
      if (/^\/build_trip/.test(path)) {
        await router.push(
          routes.share.href({ id: "magic-link", query: queryParams })
        )
        return
      }

      if (/^\/signOut/.test(path)) {
        return systemSignOut()
      }

      const match = matchRoute(path)
      switch (match?.name) {
        case "chat": {
          const chatId =
            parseInt(match.params.id?.[0] ?? "") || ("latest" as const)
          await dispatch(
            showChatView({
              router,
              chatId,
              tripSheet: queryParams.get("tripSheet") === "1",
              tripSheetEventId:
                parseInt(queryParams.get("e") ?? "") || undefined,
              placeRef: (queryParams.get("p") as PlaceRef) || undefined,
            })
          )
          return
        }
        case "saved":
          dispatch(setView({ type: "saved" }))
          await dispatch(showSavedView())
          return
        case "inspiration":
        case "explore": {
          // /explore/:name/:id redirects to the guide on the regular site.
          const inspirationRef = match.params.slug
            ?.slice(1)
            .filter(isInspirationRef)
            .at(-1)
          if (inspirationRef) {
            await dispatch(
              showGuideView(
                compact({
                  inspirationRef,
                  utm_campaign: queryParams.get("utm_campaign"),
                  utm_source: queryParams.get("utm_source"),
                  utm_medium: queryParams.get("utm_medium"),
                })
              )
            )
          } else if (match.name === "inspiration") {
            await dispatch(showInspirationView({ router }))
          } else {
            await dispatch(showExploreView({ router }))
          }
          return
        }
        case "trip": {
          const tripRef = tripRefFromUrl(path)
          if (!tripRef) break
          await dispatch(showTripView({ ref: tripRef }))
          return
        }
        case "trips":
          await dispatch(showTripsView())
          return
        case "collection": {
          const ref = match.params.slug?.at(-1)
          if (!ref || !isCollectionRef(ref)) break
          await dispatch(showCollectionView({ ref }))
          return
        }
        case "profile":
          if (match.params.handle.toLowerCase() !== user.handle) {
            await dispatch(showPublicProfileView(match.params.handle))
          } else {
            // same user display current user profile
            await dispatch(showProfileView())
          }
          return
        case "myProfile":
          await dispatch(showProfileView())
          return
        case "share": {
          const id = match.params.id as "magic-link" | PlaceRef | InspirationRef
          const code = queryParams.get("code") ?? ""
          const type = queryParams.get("type") as ShareType
          const assetMode = queryParams.get("asset_mode") ?? ""
          const partner = queryParams.get("pid") ?? ""
          const assetHandle = queryParams.get("asset_handle") ?? ""
          const assetTitle = queryParams.get("asset_title") ?? ""
          const assetUrl = queryParams.get("asset_url") ?? ""
          const assetDayCount = queryParams.get("asset_day_count") ?? ""
          const assetThumbnailUrl = queryParams.get("asset_thumbnail_url") ?? ""
          const utmProperties = getUtmParams(queryParams)

          await dispatch(
            showShareView(
              compact({
                id,
                code,
                type,
                assetMode,
                partner,
                assetHandle,
                assetTitle,
                assetUrl,
                assetDayCount,
                assetThumbnailUrl,
                utmProperties,
              })
            )
          )
          return
        }
        case "hotelRooms": {
          const placeRef = match.params.id
          if (!isPlaceRef(placeRef)) break
          const start = queryParams.get("start")
          const end = queryParams.get("end")
          const adultsParam = queryParams.get("adults")
          const childrenParam = queryParams.get("children")
          const childAgesParam = queryParams.get("childAges")
          const tripRefParam = queryParams.get("tripRef")
          const eventIdParam = queryParams.get("eventId")
          const selectedRoomIdParam = queryParams.get("selectedRoomId")
          const hotelQuoteRef = queryParams.get("hotelQuoteRef")
          const currency = queryParams.get("currency")
          const bookingError = queryParams.get("bookingError")
          const recoveryToken = queryParams.get("recoveryToken")
          await dispatch(
            showHotelRoomsView({
              placeRef,
              start,
              end,
              adults: adultsParam ? parseInt(adultsParam, 10) : null,
              children: childrenParam ? parseInt(childrenParam, 10) : null,
              childAges: childAgesParam
                ? childAgesParam.split(",").map((a) => parseInt(a, 10))
                : null,
              tripRef: (tripRefParam as TripRef) ?? null,
              eventId: eventIdParam ? parseInt(eventIdParam, 10) : null,
              selectedRoomId: selectedRoomIdParam
                ? parseInt(selectedRoomIdParam, 10)
                : null,
              hotelQuoteRef,
              currency,
              bookingError,
              recoveryToken,
            })
          )
          return
        }
        case "flightsBooking": {
          if (!/^\d+$/.test(match.params.id)) break
          await dispatch(
            showFlightBookingView({
              bookingId: +match.params.id,
              router,
            })
          )
          return
        }
        case "place": {
          const placeRef = match.params.slug?.at(-1)
          if (!placeRef || !isPlaceRef(placeRef)) break
          await dispatch(
            showPlaceView(
              compact({
                placeRef,
                router,
                utm_campaign: queryParams.get("utm_campaign"),
                utm_source: queryParams.get("utm_source"),
                utm_medium: queryParams.get("utm_medium"),
              })
            )
          )
          return
        }
        case "settings":
          await dispatch(
            showSettingsView({ section: queryParams.get("section") ?? "" })
          )
          return
        case "joinTrip":
          await dispatch(showJoinTripView({ code: match.params.code }))
          return
        case "verifyEmail":
          await dispatch(showVerifyEmailView({ code: match.params.id }))
          return
        case "authResetPassword":
          await dispatch(
            showResetPasswordView({
              resetPasswordToken: queryParams.get("token") ?? "",
              callbackUrl: queryParams.get("callbackUrl") ?? "",
            })
          )
          return
        default:
          // Every other page is unsupported in the slide-out; see below.
          break
      }

      // emitted by ActivityMonitor — marketing roots open chat in embedded
      if (isMarketingPath(path)) {
        await dispatch(showChatView({ router }))
        return
      }

      const message = `Attempted to navigate to unsupported route in embedded app, path: ${path}, url: ${
        typeof (url as unknown) === "string"
          ? url
          : safeJsonStringify(
              url,
              `[url obj that could not be stringified, type: ${typeof url}]`
            )
      }`

      // creating a real error here so we get a stack trace
      const outErr = new Error(message)
      Honeybadger.notify(outErr)
      console.error(outErr)
      void router.replace("/chat")

      toastManager.add({
        type: "error",
        description: "Action not supported in slide-out view",
      })
    } catch (e) {
      console.error("Error handling route change", e)
      toastManager.add({
        type: "error",
        description: "Unable to complete action",
      })
    } finally {
      nProgress.done()
    }
  }
)

export const handleEmailVerifySignIn = createAsyncThunk<
  void,
  { verifyCode: string },
  { dispatch: AppDispatch }
>("embeddedSlice/handleEmailVerifySignIn", async ({ verifyCode }) => {
  const clientAxios = buildClientAxios()
  const embeddedApi = new EmbeddedAuthApi(clientAxios)
  const res = await embeddedApi.verifyEmailSignIn(verifyCode)
  storeEmbeddedTokens(res)

  // reset the route so it doesn't keep looping
  const params = tryJsonParse<EmbeddedProps>(
    decodeURIComponent(mtLocation.hash.split("#")[1] ?? "")
  )
  if (params?.route) {
    params.route = "/chat"
    mtLocation.setHash("#" + encodeURIComponent(JSON.stringify(params)))
  }
  mtLocation.reload()
})
